import Decimal from 'decimal.js';
import type { ExchangeImportMeta, LedgerEvent } from './types';
import type { FiCsvDiagnosticRow } from './fiDiagnostics';
import {
  cell,
  findColIdx,
  gridFromCsvText,
  headersNormalizedRow0,
  parseDecimalCell,
  parseExchangeTs,
} from './exchangeCsvNormalize';
import { convertUsdToEur } from './eurFx';

const STABLE_USD = new Set(['USDT', 'USDC', 'BUSD', 'TUSD', 'FDUSD', 'USDP']);

export type BinanceParseStats = {
  rowsTotal: number;
  mappedAcquisition: number;
  mappedDisposal: number;
  mappedIncome: number;
  mappedInternal: number;
  unmapped: number;
  skippedEmpty: number;
  rawOperationCounts: Record<string, number>;
};

function splitTradingPair(pairRaw: string): { base: string; quote: string } | null {
  const p = String(pairRaw || '')
    .replace(/[-_/]/g, '')
    .toUpperCase();
  if (!p) return null;
  const quotes = [
    'USDT',
    'USDC',
    'BUSD',
    'TUSD',
    'FDUSD',
    'USDP',
    'BTC',
    'ETH',
    'BNB',
    'EUR',
    'GBP',
    'TRY',
    'BIDR',
    'DAI',
  ];
  for (const q of quotes) {
    if (p.endsWith(q) && p.length > q.length) {
      return { base: p.slice(0, -q.length), quote: q };
    }
  }
  return null;
}

function bump(map: Record<string, number>, key: string): void {
  map[key] = (map[key] ?? 0) + 1;
}

function meta(
  row: number,
  family: string,
  rawOp: string,
  ref?: string
): ExchangeImportMeta {
  return {
    detectedExchange: 'binance',
    detectedSchemaFamily: family,
    sourceCsvRow: row,
    rawOperationOrType: rawOp,
    sourceReference: ref,
  };
}

function pushDiag(
  diagnostics: FiCsvDiagnosticRow[],
  row: number,
  rawOp: string,
  asset: string,
  note: string
): void {
  diagnostics.push({
    source: 'binance',
    csv_row: row,
    ref: `binance_row_${row}`,
    transaction_kind: rawOp,
    asset,
    category: 'AMBIGUOUS_REVIEW',
    subreason: 'BINANCE_UNMAPPED_OR_REVIEW',
    note,
  });
}

/**
 * Binance **Spot trade history** — one row → acquisition (buy) or disposal (sell) of the base asset.
 * Quote leg EUR from `Amount` / `Total` when quote is USD-stable (Frankfurter).
 */
export async function parseBinanceSpotTradesCsv(
  text: string,
  options?: { signal?: AbortSignal; onProgress?: (m: string) => void; yieldEvery?: number }
): Promise<{
  events: LedgerEvent[];
  skipped: string[];
  diagnostics: FiCsvDiagnosticRow[];
  stats: BinanceParseStats;
}> {
  const signal = options?.signal;
  const onProgress = options?.onProgress;
  const yieldEvery = options?.yieldEvery ?? 25;
  const grid = gridFromCsvText(text);
  const skipped: string[] = [];
  const events: LedgerEvent[] = [];
  const diagnostics: FiCsvDiagnosticRow[] = [];
  const stats: BinanceParseStats = {
    rowsTotal: 0,
    mappedAcquisition: 0,
    mappedDisposal: 0,
    mappedIncome: 0,
    mappedInternal: 0,
    unmapped: 0,
    skippedEmpty: 0,
    rawOperationCounts: {},
  };

  if (grid.length < 2) {
    skipped.push('Binance spot: no data rows');
    return { events, skipped, diagnostics, stats };
  }

  const h = headersNormalizedRow0(grid);
  const ixTime =
    findColIdx(h, [/utc_time/, /date.*utc/, /^time$/, /^date$/]) ?? findColIdx(h, [/datetime/]);
  const ixPair = findColIdx(h, [/^pair$/, /trading_pair/, /symbol/]);
  const ixSide = findColIdx(h, [/^side$/, /^type$/]);
  const ixExecuted = findColIdx(h, [/executed/, /filled/, /^quantity$/]);
  const ixAmount = findColIdx(h, [/^amount$/, /total/, /quote_qty/]);
  const ixFee = findColIdx(h, [/^fee$/]);
  const ixFeeCoin = findColIdx(h, [/fee.*coin/, /commission_asset/]);
  const ixOrder = findColIdx(h, [/order_id/, /^order$/]);

  if (ixPair === undefined || ixSide === undefined || ixExecuted === undefined) {
    skipped.push('Binance spot: missing Pair/Side/Executed columns for this variant');
    return { events, skipped, diagnostics, stats };
  }

  const family = 'binance_spot_trade_history';
  const dataRows = grid.length - 1;
  stats.rowsTotal = dataRows;

  for (let r = 1; r < grid.length; r++) {
    if (signal?.aborted) {
      skipped.push(`Binance spot: stopped at row ${r}`);
      break;
    }
    if (r % yieldEvery === 0) {
      await new Promise((res) => setTimeout(res, 0));
      onProgress?.(`Binance spot: row ${r}/${dataRows}…`);
    }

    const row = grid[r]!;
    const pair = cell(row, ixPair);
    const sideRaw = cell(row, ixSide).toUpperCase();
    const executed = parseDecimalCell(cell(row, ixExecuted));
    const amountTotal = ixAmount !== undefined ? parseDecimalCell(cell(row, ixAmount)) : null;
    const tss = ixTime !== undefined ? parseExchangeTs(cell(row, ixTime)) : null;

    if (!pair || executed == null || !executed.gt(0) || tss == null) {
      stats.skippedEmpty++;
      continue;
    }

    bump(stats.rawOperationCounts, sideRaw || '?');

    const split = splitTradingPair(pair);
    if (!split) {
      stats.unmapped++;
      pushDiag(diagnostics, r, sideRaw, pair, 'Could not parse trading pair for spot row');
      continue;
    }

    const { base, quote } = split;
    const date = new Date(tss);
    const orderRef = ixOrder !== undefined ? cell(row, ixOrder) : '';

    const quoteIsStable = STABLE_USD.has(quote) || quote === 'USD';
    let eurLeg: Decimal | null = null;
    if (amountTotal != null && amountTotal.gt(0) && quoteIsStable) {
      eurLeg = await convertUsdToEur(amountTotal.abs(), date);
      if (signal?.aborted) break;
    }

    const refBase = `binance_spot_${r}_${sideRaw}_${base}`;
    const exM = meta(r, family, sideRaw, orderRef || undefined);

    if (sideRaw.startsWith('BUY')) {
      const ev: LedgerEvent = {
        ts: tss,
        source: 'binance',
        kind: 'acquisition',
        asset: base,
        amount: executed.abs(),
        eurValue: eurLeg,
        ref: refBase,
        classification: 'TRANSFER_IN',
        confidence: eurLeg ? 'MEDIUM' : 'LOW',
        explanation: `Binance spot BUY ${pair}: acquired ${base} (quote ${quote}). EUR from quote total when USD-stable.`,
        exchangeImport: exM,
        csvMeta: {
          timestampUtc: ixTime !== undefined ? cell(row, ixTime) : '',
          transactionDescription: `Spot ${sideRaw} ${pair}`,
          currency: base,
          amount: executed.toFixed(),
          toCurrency: quote,
          toAmount: amountTotal?.toFixed() ?? '',
          nativeCurrency: quote,
          nativeAmount: amountTotal?.toFixed() ?? '',
          nativeAmountUsd: quoteIsStable && amountTotal ? amountTotal.toFixed() : '',
          transactionKind: `SPOT_${sideRaw}`,
          transactionHash: orderRef,
        },
        reviewFlags: eurLeg ? ['binance_spot_buy'] : ['binance_spot_buy', 'exchange_csv_missing_eur'],
      };
      events.push(ev);
      stats.mappedAcquisition++;
    } else if (sideRaw === 'SELL') {
      const ev: LedgerEvent = {
        ts: tss,
        source: 'binance',
        kind: 'disposal',
        asset: base,
        amount: executed.abs(),
        eurValue: eurLeg,
        ref: `${refBase}_sell`,
        classification: 'TRANSFER_OUT',
        confidence: eurLeg ? 'MEDIUM' : 'LOW',
        explanation: `Binance spot SELL ${pair}: disposed ${base} (quote ${quote}).`,
        exchangeImport: exM,
        csvMeta: {
          timestampUtc: ixTime !== undefined ? cell(row, ixTime) : '',
          transactionDescription: `Spot ${sideRaw} ${pair}`,
          currency: base,
          amount: executed.toFixed(),
          toCurrency: quote,
          toAmount: amountTotal?.toFixed() ?? '',
          nativeCurrency: quote,
          nativeAmount: amountTotal?.toFixed() ?? '',
          nativeAmountUsd: quoteIsStable && amountTotal ? amountTotal.toFixed() : '',
          transactionKind: `SPOT_${sideRaw}`,
          transactionHash: orderRef,
        },
        reviewFlags: eurLeg ? ['binance_spot_sell'] : ['binance_spot_sell', 'exchange_csv_missing_eur'],
      };
      events.push(ev);
      stats.mappedDisposal++;
    } else {
      stats.unmapped++;
      pushDiag(diagnostics, r, sideRaw, base, 'Unexpected spot side; not BUY/SELL');
    }

    const fee = ixFee !== undefined ? parseDecimalCell(cell(row, ixFee)) : null;
    const feeCoin =
      ixFeeCoin !== undefined ? cell(row, ixFeeCoin).toUpperCase() : quote;
    if (fee != null && fee.gt(0) && feeCoin) {
      const feeEur = STABLE_USD.has(feeCoin)
        ? await convertUsdToEur(fee, date)
        : null;
      if (signal?.aborted) break;
      events.push({
        ts: tss,
        source: 'binance',
        kind: 'disposal',
        asset: feeCoin,
        amount: fee.abs(),
        eurValue: feeEur,
        ref: `${refBase}_fee`,
        classification: 'TRANSFER_OUT',
        confidence: feeEur ? 'LOW' : 'LOW',
        explanation: 'Binance spot trading fee',
        exchangeImport: meta(r, family, `FEE_${sideRaw}`, orderRef || undefined),
        reviewFlags: feeEur ? ['binance_spot_fee'] : ['binance_spot_fee', 'exchange_csv_missing_eur'],
      });
      stats.mappedDisposal++;
    }
  }

  return { events, skipped, diagnostics, stats };
}

const INTERNAL_RE = /internal transfer|transfer_in|transfer_out|transfer between|universal transfer|sub.account|sub_account|margin|isolated|cross margin|futures transfer|pool/i;
const INCOME_RE = /staking|interest|reward|rebate|commission|airdrop|launchpool|earn|simple earn|distribution|savings|defi|liquid swap|mining|referee|referral/i;
const DEPOSIT_RE = /^deposit|fiat deposit|buy crypto|p2p|purchase crypto|fiat.*deposit/i;
const WITHDRAW_RE = /^withdraw|crypto withdrawal|fiat withdrawal|send out/i;

/**
 * Binance **Transaction history / Asset history** — deposits, withdrawals, transfers, rewards.
 */
export async function parseBinanceTransactionHistoryCsv(
  text: string,
  options?: { signal?: AbortSignal; onProgress?: (m: string) => void; yieldEvery?: number }
): Promise<{
  events: LedgerEvent[];
  skipped: string[];
  diagnostics: FiCsvDiagnosticRow[];
  stats: BinanceParseStats;
}> {
  const signal = options?.signal;
  const onProgress = options?.onProgress;
  const yieldEvery = options?.yieldEvery ?? 25;
  const grid = gridFromCsvText(text);
  const skipped: string[] = [];
  const events: LedgerEvent[] = [];
  const diagnostics: FiCsvDiagnosticRow[] = [];
  const stats: BinanceParseStats = {
    rowsTotal: 0,
    mappedAcquisition: 0,
    mappedDisposal: 0,
    mappedIncome: 0,
    mappedInternal: 0,
    unmapped: 0,
    skippedEmpty: 0,
    rawOperationCounts: {},
  };

  if (grid.length < 2) {
    skipped.push('Binance tx history: no data rows');
    return { events, skipped, diagnostics, stats };
  }

  const h = headersNormalizedRow0(grid);
  const ixTime =
    findColIdx(h, [/^utc_time$/, /insert_time/, /time_utc/, /^time$/, /date.*utc/]) ??
    findColIdx(h, [/^date$/]);
  const ixOp = findColIdx(h, [/^operation$/]);
  const ixCoin = findColIdx(h, [/^coin$/]);
  const ixChange = findColIdx(h, [/^change$/]);
  const ixRemark = findColIdx(h, [/remark/, /note/, /description/]);
  const ixTx = findColIdx(h, [/txid/, /tx_id/, /transaction_id/]);

  if (ixOp === undefined || ixCoin === undefined || ixChange === undefined) {
    skipped.push('Binance tx history: missing Operation/Coin/Change');
    return { events, skipped, diagnostics, stats };
  }

  const family = 'binance_transaction_history';
  const dataRows = grid.length - 1;
  stats.rowsTotal = dataRows;

  for (let r = 1; r < grid.length; r++) {
    if (signal?.aborted) {
      skipped.push(`Binance tx history: stopped at row ${r}`);
      break;
    }
    if (r % yieldEvery === 0) {
      await new Promise((res) => setTimeout(res, 0));
      onProgress?.(`Binance tx history: row ${r}/${dataRows}…`);
    }

    const row = grid[r]!;
    const op = cell(row, ixOp);
    const opNorm = op.toLowerCase().trim();
    const coin = cell(row, ixCoin).toUpperCase().replace(/\s/g, '');
    const chg = parseDecimalCell(cell(row, ixChange));
    const tss = ixTime !== undefined ? parseExchangeTs(cell(row, ixTime)) : null;
    const remark = ixRemark !== undefined ? cell(row, ixRemark) : '';
    const txid = ixTx !== undefined ? cell(row, ixTx) : '';

    bump(stats.rawOperationCounts, op || '(empty)');

    if (!coin || chg == null || chg.eq(0) || tss == null) {
      stats.skippedEmpty++;
      continue;
    }

    const date = new Date(tss);
    const absAmt = chg.abs();
    const signedPositive = chg.gt(0);
    const ref = `binance_tx_${r}_${coin}`;
    const exM = meta(r, family, op, txid || undefined);

    let eurVal: Decimal | null = null;
    if (STABLE_USD.has(coin) || coin === 'USD') {
      eurVal = await convertUsdToEur(absAmt, date);
      if (signal?.aborted) break;
    }

    if (INTERNAL_RE.test(opNorm) || INTERNAL_RE.test(remark.toLowerCase())) {
      events.push({
        ts: tss,
        source: 'binance',
        kind: 'internal_transfer',
        asset: coin,
        amount: absAmt,
        eurValue: null,
        ref,
        classification: 'INTERNAL_TRANSFER',
        fiTaxCategory: 'NON_TAXABLE_INTERNAL',
        fiSubreason: 'BINANCE_INTERNAL_OR_TRANSFER',
        explanation: `Binance internal/transfer: ${op}. ${remark}`.trim(),
        exchangeImport: exM,
        reviewFlags: ['binance_internal_transfer'],
      });
      stats.mappedInternal++;
      continue;
    }

    if (INCOME_RE.test(opNorm)) {
      events.push({
        ts: tss,
        source: 'binance',
        kind: 'income',
        asset: coin,
        amount: absAmt,
        eurValue: eurVal,
        ref,
        classification: 'INCOME',
        fiTaxCategory: 'TAXABLE_INCOME',
        fiSubreason: 'BINANCE_REWARD_OR_INTEREST',
        explanation: `Binance income-like: ${op}`,
        exchangeImport: exM,
        reviewFlags: eurVal ? ['binance_income'] : ['binance_income', 'exchange_csv_missing_eur'],
      });
      stats.mappedIncome++;
      continue;
    }

    if (DEPOSIT_RE.test(opNorm) && signedPositive) {
      events.push({
        ts: tss,
        source: 'binance',
        kind: 'acquisition',
        asset: coin,
        amount: absAmt,
        eurValue: eurVal,
        ref,
        classification: 'TRANSFER_IN',
        explanation: `Binance deposit: ${op}`,
        exchangeImport: exM,
        reviewFlags: eurVal ? [] : ['exchange_csv_missing_eur'],
      });
      stats.mappedAcquisition++;
      continue;
    }

    if (WITHDRAW_RE.test(opNorm) && !signedPositive) {
      events.push({
        ts: tss,
        source: 'binance',
        kind: 'disposal',
        asset: coin,
        amount: absAmt,
        eurValue: eurVal,
        ref,
        classification: 'TRANSFER_OUT',
        explanation: `Binance withdrawal: ${op}`,
        exchangeImport: exM,
        reviewFlags: eurVal ? [] : ['exchange_csv_missing_eur'],
      });
      stats.mappedDisposal++;
      continue;
    }

    if (opNorm.includes('fee') && !signedPositive) {
      events.push({
        ts: tss,
        source: 'binance',
        kind: 'disposal',
        asset: coin,
        amount: absAmt,
        eurValue: eurVal,
        ref: `${ref}_fee`,
        classification: 'TRANSFER_OUT',
        explanation: `Binance fee: ${op}`,
        exchangeImport: exM,
        reviewFlags: ['binance_fee', ...(eurVal ? [] : ['exchange_csv_missing_eur'])],
      });
      stats.mappedDisposal++;
      continue;
    }

    stats.unmapped++;
    pushDiag(
      diagnostics,
      r,
      op,
      coin,
      `Unmapped Binance operation — review manually (Change ${chg.toFixed()}).`
    );

    events.push({
      ts: tss,
      source: 'binance',
      kind: 'acquisition',
      asset: coin,
      amount: absAmt,
      eurValue: null,
      ref: `${ref}_review`,
      classification: 'UNKNOWN_REVIEW',
      confidence: 'LOW',
      explanation: `Binance unmapped operation: ${op}. ${remark}`.trim(),
      exchangeImport: exM,
      reviewFlags: ['binance_unmapped_operation', 'exchange_csv_needs_review'],
    });
  }

  return { events, skipped, diagnostics, stats };
}
